'use client'

import Input from '@components/primitives/Input'
import { Text } from '@components/primitives/Text'
import { UnitConversion } from '@lib/utilities/UnitConversion'
import { useState } from 'react'
import type { DemographicsCollectElementProps } from './types'

export default function CollectHeight({
  patientProfileData: { heightInCm },
}: DemographicsCollectElementProps) {
  const heightInImperial = heightInCm[0]
    ? UnitConversion.centimetersToFeetAndInches(parseInt(heightInCm[0]))
    : undefined

  const [feet, setFeet] = useState<string | undefined>(
    heightInImperial?.feet.toString(),
  )
  const [inches, setInches] = useState<string | undefined>(
    heightInImperial?.inches.toString(),
  )

  const [currentHeightInCm, setCurrentHeightInCm] = useState<
    string | undefined
  >(heightInCm[0])

  const updateCurrentHeightInCm = (
    feetToConvert: string | undefined,
    inchesToConvert: string | undefined,
  ) => {
    // nullish coalescing operator will return true if feetToConvert is not null or undefined
    if (feetToConvert ?? (feetToConvert && inchesToConvert)) {
      setCurrentHeightInCm(
        UnitConversion.feetAndInchesToCentimeters(
          parseInt(feetToConvert),
          parseInt(inchesToConvert ?? '0'),
        ).toString(),
      )
    }
  }

  const handleFeetChange = (feetFromInput: string) => {
    if (!feetFromInput) {
      setFeet('')
      return
    }

    const enforcedFeet = enforceMinMax({
      max: 8,
      min: 0,
      value: parseInt(feetFromInput),
    })
    setFeet(enforcedFeet.toString())

    updateCurrentHeightInCm(enforcedFeet.toString(), inches)

    // if there is a value in feet, then lets focus them to the inches input
    if (feetFromInput) {
      // TODO: the Input component should support refs and we should  use a ref instead of getElementById
      const input = document.getElementById('inches')
      input?.focus()
    }
  }

  const handleInchesChange = (inchesFromInput: string) => {
    if (!inchesFromInput) {
      setInches('')
      return
    }
    const enforcedInches = enforceMinMax({
      max: 11,
      min: 0,
      value: parseInt(inchesFromInput),
    })
    setInches(enforcedInches.toString())
    updateCurrentHeightInCm(feet, enforcedInches.toString())
  }

  return (
    <div className='mx-auto flex w-max flex-col gap-2'>
      <Text
        className='text-neutral900'
        element={'p'}
        styleName='h3'
        value='How tall are you?'
      />
      <input name='heightInCm' type='hidden' value={currentHeightInCm} />
      <div className='flex items-baseline gap-2'>
        <div className='flex flex-col'>
          <Text
            className='text-neutral900'
            element={'p'}
            styleName='p-small'
            value='Feet'
          />
          <Input
            autoFocus={true}
            className='h-14 rounded-md border-neutral200 bg-neutral50 text-neutral900'
            inputMode='numeric'
            max={8}
            min={0}
            onChange={(e) => handleFeetChange(e.target.value)}
            placeholder='ft'
            type='number'
            value={feet}
          />
        </div>
        <div className='flex flex-col'>
          <Text
            className='text-neutral900'
            element={'p'}
            styleName='p-small'
            value='Inches'
          />
          <Input
            className='h-14 rounded-md border-neutral200 bg-neutral50 text-neutral900'
            id='inches'
            inputMode='numeric'
            max={11}
            min={0}
            onChange={(e) => handleInchesChange(e.target.value)}
            placeholder='in'
            type='number'
            value={inches}
          />
        </div>
      </div>
    </div>
  )
}

function enforceMinMax({
  max,
  min,
  value,
}: {
  max: number
  min: number
  value: number
}) {
  if (value > max) {
    return max
  } else if (value < min) {
    return min
  } else {
    return value
  }
}
